import { useMemo, useState } from 'react'
import { useParams, useSearchParams, Navigate } from 'react-router-dom'
import { fbt, useLocaleContext } from 'fbtee'
import { toast } from 'sonner'
import { useAuth } from '../../hooks/useAuth'
import { useUserProfiles } from '../../hooks/useUserProfiles'
import { useUserEventInterests } from '../../hooks/useUserEventInterests'
import { attendingEventIds, useEventsByIds } from '../../hooks/useEventsByIds'
import { useFriendships } from '../../hooks/useFriendships'
import { useFavoriteFriends } from '../../contexts/FavoriteFriendsContext'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { useArtistInterests } from '../../hooks/useArtistInterests'
import { useArtistsByIds } from '../../hooks/useArtistsByIds'
import { useFriendArtistInterests } from '../../hooks/useFriendArtistInterests'
import { useGroups } from '../../hooks/useGroups'
import { pageTitle, useDocumentTitle } from '../../hooks/useDocumentTitle'
import { useTrackView } from '../../hooks/useTrackView'
import { useFilteredEventsByStatus } from '../../hooks/useFilteredEventsByStatus'
import { usePageLoadTrace } from '../../lib/perf'
import { beaconUserFallback, userUnblockedToast } from '../../lib/commonStrings'
import { rsvpEventPrompt, interestPrompt, favoritePrompt } from '../../lib/guestPrompts'
import { track } from '../../lib/analytics'
import { useBlockedUsers } from '../../contexts/BlockedUsersContext'
import { PageLayout } from '../../components/PageLayout'
import { UserAvatar } from '../../components/UserAvatar'
import { NotFoundState } from '../../components/NotFoundState'
import { ProfileHero } from '../../components/profile/ProfileHero'
import { ProfileStats } from '../../components/profile/ProfileStats'
import { buildProfileStats } from '../../components/profile/buildProfileStats'
import { Skeleton } from '../../components/ui/skeleton'
import { ProfilePillNav } from '../../components/profile/ProfilePillNav'
import { PROFILE_TABS, type ProfileTab } from '../../components/profile/profileTabs'
import { ProfileEventsView } from '../../components/profile/ProfileEventsView'
import { ProfileArtistsView } from '../../components/profile/ProfileArtistsView'
import { useSocialEventData } from '../../hooks/useSocialEventData'
import { RelationshipActions, type FriendState } from './RelationshipActions'
import { TogetherCard } from './TogetherCard'
import { ModerationDialogs } from './ModerationDialogs'
import { GuestProfileGate } from './GuestProfileGate'

const NO_IDS: string[] = []

const ATTENDING = new Set(['going', 'interested'])

export default function UserProfilePage() {
  const { userId } = useParams<{ userId: string }>()
  const { locale } = useLocaleContext()
  const { user, signedInUid, loading: authLoading } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as ProfileTab
  const [tab, setTab] = useState<ProfileTab>(PROFILE_TABS.includes(tabParam) ? tabParam : 'events')
  const [busy, setBusy] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false)
  const { unblockUser, isBlocked } = useBlockedUsers()

  const isSelf = user?.uid === userId

  const { friendIdSet, incoming, outgoing, loading: friendsLoading, sendRequest, acceptRequest } =
    useFriendships(signedInUid)
  const { favoriteIdSet, toggleFavorite } = useFavoriteFriends()
  const viewedProfiles = useUserProfiles(userId ? [userId] : [])
  const profile = userId ? viewedProfiles[userId] : undefined

  const { eventStatuses: viewedStatuses, loading: interestsLoading } = useUserEventInterests(userId ?? null)
  const { eventStatuses: myStatuses, setEventInterest } = useUserEventInterests(signedInUid)
  const { facepileData, mergedProfiles, friendIds } = useSocialEventData(myStatuses)
  const { guard } = useGuestGuard()
  const guardedSetInterest = guard(setEventInterest, rsvpEventPrompt())

  const viewedEventIds = useMemo(() => attendingEventIds(viewedStatuses), [viewedStatuses])
  const { events, pending: eventsLoading } = useEventsByIds(viewedEventIds)
  const { goingEvents, interestedEvents, pastGoingEvents } = useFilteredEventsByStatus(events, viewedStatuses)

  // Their artists for display; the viewer's own for the heart and the overlap.
  const { interestedArtistIds: viewedArtistIds, loading: viewedArtistsLoading } =
    useArtistInterests(userId ?? null)
  const { interestedArtistIds: myArtistIds, setArtistInterest } = useArtistInterests(signedInUid)
  const guardedSetArtist = guard(
    (artistId: string, marked: boolean) => setArtistInterest(artistId, marked),
    interestPrompt('artist_interest'),
  )
  const viewedArtistList = useMemo(() => [...viewedArtistIds], [viewedArtistIds])
  const { artists, loading: artistsLoading } = useArtistsByIds(viewedArtistList)
  // Every friend's artist interests, app-wide — only worth reading on the Artists tab.
  const { artistUserIds } = useFriendArtistInterests(tab === 'artists' ? friendIds : NO_IDS)
  const { groups: myGroups } = useGroups(signedInUid)

  const together = useMemo(() => {
    const events = [...goingEvents, ...interestedEvents].filter(e => ATTENDING.has(myStatuses.get(e.id) ?? ''))
    const artistIds = viewedArtistList.filter(id => myArtistIds.has(id))
    return {
      eventCount: events.length,
      eventImages: events.map(e => e.imageUrl),
      artistCount: artistIds.length,
      artistImages: artistIds.map(id => artists[id]?.imageUrl),
      groups: myGroups.filter(g => userId && (g.memberIds.includes(userId) || g.guestIds?.includes(userId))),
    }
  }, [goingEvents, interestedEvents, myStatuses, viewedArtistList, myArtistIds, artists, myGroups, userId])

  const loading = interestsLoading || eventsLoading || friendsLoading
  usePageLoadTrace('user_profile_page_to_content', !loading)

  const displayName = profile?.displayName?.trim()
  useDocumentTitle(displayName ? pageTitle(displayName) : null)
  useTrackView('user_profile_viewed', userId ? {} : null)

  if (isSelf) return <Navigate to="/profile" replace />
  // Profiles are only readable with a session; a signed-out visitor gets a way in, not a 404.
  if (!authLoading && !user) return <GuestProfileGate />
  if (!loading && !profile) {
    return <NotFoundState message={fbt('User not found', 'Label when a user profile does not exist')} />
  }

  function handleTabChange(t: ProfileTab) {
    setTab(t)
    setSearchParams(t === 'events' ? {} : { tab: t }, { replace: true })
    track('profile_tab_changed', { tab: t })
  }

  async function withBusy(fn: () => Promise<unknown>) {
    setBusy(true)
    try {
      await fn()
    } finally {
      setBusy(false)
    }
  }

  const friendState: FriendState = !userId
    ? 'none'
    : friendIdSet.has(userId)
      ? 'friends'
      : outgoing.some(f => f.userIds.includes(userId))
        ? 'outgoing'
        : incoming.some(f => f.userIds.includes(userId))
          ? 'incoming'
          : 'none'

  return (
    <PageLayout back>
      <ProfileHero
        loading={loading && !profile}
        avatar={<UserAvatar profile={profile} size="2xl" />}
        actions={
          authLoading || friendsLoading ? (
            <Skeleton className="h-9 flex-1" />
          ) : (
            userId && (
              <RelationshipActions
                friendState={friendState}
                busy={busy}
                onAdd={guard(() => withBusy(() => sendRequest(userId, 'profile')), {
                  intent: 'add_friend',
                  title: fbt('Log in to add friends', 'Title of the upsell dialog when a guest taps add friend on a profile'),
                  description: fbt("Log in to become friends and see each other's festival picks.", 'Body of the upsell dialog when a guest taps add friend on a profile'),
                })}
                onAccept={() => withBusy(() => acceptRequest(userId))}
                favorite={favoriteIdSet.has(userId)}
                onToggleFavorite={guard(() => toggleFavorite(userId, 'profile'), favoritePrompt())}
                canModerate={!!signedInUid}
                isBlocked={isBlocked(userId)}
                onReport={() => setReportOpen(true)}
                onBlock={() => setBlockConfirmOpen(true)}
                onUnblock={async () => {
                  await unblockUser(userId)
                  toast.success(userUnblockedToast())
                }}
              />
            )
          )
        }
      >
        {loading && !displayName ? (
          <Skeleton className="h-7 w-32" />
        ) : (
          <h2 className="text-xl font-semibold">{displayName || beaconUserFallback()}</h2>
        )}
        <ProfileStats
          loading={interestsLoading || eventsLoading || viewedArtistsLoading}
          stats={buildProfileStats({
            pastEvents: pastGoingEvents,
            artistCount: viewedArtistIds.size,
            onArtistsClick: () => handleTabChange('artists'),
          })}
        />
      </ProfileHero>

      <TogetherCard
        sharedEventCount={together.eventCount}
        sharedEventImages={together.eventImages}
        sharedArtistCount={together.artistCount}
        sharedArtistImages={together.artistImages}
        sharedGroups={together.groups}
        onShowEvents={() => handleTabChange('events')}
        onShowArtists={() => handleTabChange('artists')}
      />

      <ProfilePillNav tab={tab} onTabChange={handleTabChange} />

      {tab === 'events' && (
        <ProfileEventsView
          goingEvents={goingEvents}
          interestedEvents={interestedEvents}
          pastEvents={pastGoingEvents}
          myStatuses={myStatuses}
          onSetInterest={(eventId, next) => guardedSetInterest(eventId, next)}
          facepileData={facepileData}
          profiles={mergedProfiles}
          currentUserId={user?.uid ?? null}
          loading={loading}
          locale={locale}
        />
      )}

      {tab === 'artists' && (
        <ProfileArtistsView
          artistIds={viewedArtistList}
          artists={artists}
          myInterestedArtistIds={myArtistIds}
          onSetArtist={guardedSetArtist}
          artistUserIds={artistUserIds}
          userProfiles={mergedProfiles}
          viewedUserId={userId}
          loading={viewedArtistsLoading || artistsLoading}
          isSelf={false}
        />
      )}

      {userId && (
        <ModerationDialogs
          userId={userId}
          reportOpen={reportOpen}
          onReportOpenChange={setReportOpen}
          blockOpen={blockConfirmOpen}
          onBlockOpenChange={setBlockConfirmOpen}
        />
      )}
    </PageLayout>
  )
}
